import { computed, inject, Injectable, signal } from "@angular/core";
import { UsersService } from "../Services/users.service";
import { UsersService as IntuneUsersService } from "../Services/Intune/users.service";
import { httpResource } from "@angular/common/http";
import { CreateUserPayload, ShowResponse, UpdateUserPayload } from "../Interfaces/api/users.interface";
import { firstValueFrom } from "rxjs";
import { IndexResponse } from "../Interfaces/api/Intune/users.interface";
import { User } from "../Models/avyna/user.model";
import { IntuneUser } from "../Models/Intune/intune-user.model";
import { SearchService } from "../Services/Intune/search.service";
import { RolesService } from "../Services/roles.service";
import { ShowResponse as RoleShowResponse } from "../Interfaces/api/roles.interface";
import { Permission } from "../Models/avyna/permission.model";
import { ListUsersFacade } from "./list-users.facade";
import { ResourceStateService } from "../Modules/Shared/Services/resource-state.service";


@Injectable()
export class UserFormFacade {
    private readonly _resourceState = inject(ResourceStateService);

    private _userService = inject(UsersService);
    private _intuneUsersService = inject(IntuneUsersService);
    private _searchService = inject(SearchService);
    private _rolesService = inject(RolesService);
    private readonly _userId = signal<string | null>(null);
    private readonly _searchUserTerm = signal<string>('');
    private readonly _selectedUser = signal<IntuneUser | null>(null);
    private readonly _entitiesSearchTerm = signal<string>('');
    private readonly _selectedRoleId = signal<string>('');

    private readonly _listUsersFacade = inject(ListUsersFacade);

    private readonly _baseResource = httpResource<ShowResponse>(() => {
        const userId = this._userId();
        if (userId) return this._userService.show(userId);
        return undefined;
    });

    private readonly _searchUsersResource = httpResource<IndexResponse>(() => {
        const term = this._searchUserTerm();
        if (term && term.length > 2) return this._intuneUsersService.index({ unactiveAvynaUsers: true, search: term });
        return undefined;
    });

    private readonly _searchEntitiesResource = httpResource<any>(() => {
        const term = this._entitiesSearchTerm();
        if (term && term.length > 2) return this._searchService.index(term, { users: '1', groups: '1', devices: '0' });
        return undefined;
    });

    private readonly _selectedRoleResource = httpResource<RoleShowResponse>(() => {
        const id = this._selectedRoleId();
        if (id) return this._rolesService.show(id);
        return undefined;
    });


    public setUserId(userId: string | null) {
        if (this._userId() === userId) {
            if (userId) this._baseResource.reload();
        } else {
            this._userId.set(userId);
        }
    }

    public setSearchTerm(term: string) {
        this._searchUserTerm.set(term);
    }

    public setSelectedUser(user: IntuneUser | null) {
        this._selectedUser.set(user);
    }

    public setSelectedRoleId(id: string) {
        this._selectedRoleId.set(id);
    }

    public async createUser(payload: CreateUserPayload): Promise<void> {
        await firstValueFrom(this._userService.create(payload));
        this._listUsersFacade.reload();
    }

    public async updateUser(payload: UpdateUserPayload): Promise<void> {
        const id = this._userId();
        if (id) {
            await firstValueFrom(this._userService.update(id, payload));
            this._listUsersFacade.reload();
        }
    }

    public async removeSeat(): Promise<void> {
        const id = this._userId();
        if (id) {
            await firstValueFrom(this._userService.removeSeat(id));
            this._listUsersFacade.reload();
        }
    }

    readonly user = computed((): User => this._resourceState.valueOf(this._baseResource)?.data ?? new User());
    readonly searchUserResults = computed((): IntuneUser[] => {
        const data = this._resourceState.valueOf(this._searchUsersResource)?.data;
        if (!data) return [];
        return Array.isArray(data) ? data : (data.value ?? []);
    });
    readonly selectedUser = computed((): IntuneUser | null => this._selectedUser());
    readonly searchEntitiesResults = computed(() => this._resourceState.valueOf(this._searchEntitiesResource)?.data);

    readonly isUserLoading = computed(() => this._baseResource.isLoading());
    readonly isSearchEntitiesLoading = computed(() => this._searchEntitiesResource.isLoading());
    readonly isSearchUserLoading = computed(() => this._searchUsersResource.isLoading());
    readonly selectedRolePermissions = computed<Permission[]>(() =>
        this._resourceState.valueOf(this._selectedRoleResource)?.data?.permissions ?? []
    );
    readonly isPermissionsLoading = computed(() => this._selectedRoleResource.isLoading());
}